import React from "react";
import { Box, Button, Chip, Slider, Tooltip, Typography } from "@mui/material";

import { curingLabel } from "../../api/curing";
import type { CuringForDate } from "../../types/curing";

interface CuringControlProps {
  /** Current value in percent. */
  value: number;
  onChange: (value: number) => void;
  /** What the project knows about curing on the simulation's date. */
  forDate?: CuringForDate | null;
  /** False while the user is overriding the satellite value by hand. */
  usingEstimate: boolean;
  onUseEstimate: (use: boolean) => void;
  /** Dark weather-panel styling, matching the surrounding sliders. */
  dark?: boolean;
}

/**
 * Grass curing for a simulation.
 *
 * Two states, driven by whether a satellite observation covers the date:
 *
 *  - Inside the window, the measured value is used and the slider is locked
 *    until you choose to override it. A measurement beats a guess.
 *  - Outside it, there is no measured value. Rather than quietly reusing a
 *    stale one, the control says so and asks you to set it, starting from a
 *    typical value for the time of year.
 */
const CuringControl: React.FC<CuringControlProps> = ({
  value,
  onChange,
  forDate,
  usingEstimate,
  onUseEstimate,
  dark = true,
}) => {
  const hasEstimate = Boolean(forDate?.within_window && forDate?.curing !== null);
  const locked = hasEstimate && usingEstimate;

  const subtle = dark ? "rgba(255, 255, 255, 0.6)" : "text.secondary";
  const faint = dark ? "rgba(255, 255, 255, 0.5)" : "text.disabled";
  const strong = dark ? "#fff" : "text.primary";

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: subtle, textTransform: "uppercase", fontSize: "0.75rem" }}
          >
            Curing
          </Typography>
          <Typography variant="h4" sx={{ color: strong, fontWeight: 600 }}>
            {value}%
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 600 }}>
            {curingLabel(value)}
          </Typography>
          <Typography variant="caption" sx={{ color: faint, display: "block" }}>
            How much of the grass has dried off
          </Typography>
        </Box>
      </Box>

      {hasEstimate && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Tooltip
            title={
              `Measured from satellite imagery of this project on ` +
              `${forDate?.observed_on}. Grass curing changes slowly, so this ` +
              `reading is used for simulations within two weeks of it.`
            }
          >
            <Chip
              size="small"
              icon={<span className="material-icons" style={{ fontSize: 16 }}>satellite_alt</span>}
              label={`Satellite ${forDate?.curing}%, seen ${forDate?.observed_on}`}
              color={usingEstimate ? "success" : "default"}
              variant={usingEstimate ? "filled" : "outlined"}
            />
          </Tooltip>
          <Button
            size="small"
            onClick={() => onUseEstimate(!usingEstimate)}
            sx={{ color: dark ? "rgba(255,255,255,0.7)" : undefined }}
          >
            {usingEstimate ? "Set it myself" : "Use the satellite reading"}
          </Button>
        </Box>
      )}

      {!hasEstimate && (
        <Typography variant="caption" sx={{ color: faint, display: "block", mb: 1.5 }}>
          {forDate?.observed_on
            ? `No satellite reading covers this date. The most recent one is from ` +
              `${forDate.observed_on}, too far back to rely on, so please set the ` +
              `curing you expect.`
            : `No satellite reading is available for this project yet, so please ` +
              `set the curing you expect.`}
        </Typography>
      )}

      <Box sx={{ mt: "auto" }}>
      <Slider
        value={value}
        disabled={locked}
        onChange={(_, next) => onChange(next as number)}
        min={0}
        max={100}
        step={1}
        sx={{
          color: "#4caf50",
          "& .MuiSlider-thumb": { width: 20, height: 20 },
          "& .MuiSlider-track": { height: 6 },
          "& .MuiSlider-rail": {
            height: 6,
            bgcolor: dark ? "rgba(255, 255, 255, 0.1)" : undefined,
          },
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" sx={{ color: faint }}>
          0% (green)
        </Typography>
        <Typography variant="caption" sx={{ color: faint }}>
          50%
        </Typography>
        <Typography variant="caption" sx={{ color: faint }}>
          100% (fully cured)
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default CuringControl;
